import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { canWrite } from '@renalflow/core';
import { Button } from '../components/Button';
import { useDb } from '../db/hooks';
import { signOut } from '../auth/session';
import { hasCurrentConsent } from './consent';
import { cachedEntitlement, fetchEntitlement, loadEntitlement, type Entitlement } from './entitlement';
import { DisclaimerPage } from './DisclaimerPage';
import { PaywallPage } from './PaywallPage';

interface AccountContextValue {
  entitlement: Entitlement;
  refreshEntitlement: () => Promise<void>;
}

const AccountContext = createContext<AccountContextValue | null>(null);

export function useAccount() {
  const value = useContext(AccountContext);
  if (!value) throw new Error('useAccount() must be used inside <AccountGate>.');
  return value;
}

/** Optional variant for components that also render in demo mode. */
export function useOptionalAccount() {
  return useContext(AccountContext);
}

type GateState =
  | { step: 'checking' }
  | { step: 'consent' }
  | { step: 'ready'; entitlement: Entitlement }
  | { step: 'error'; message: string };

/**
 * After sign-in: the user must accept the disclaimer and health-data consent, then have a plan
 * (SPEC.md §5 first run, §6.2). A lapsed yearly plan gets in, but read-only.
 */
export function AccountGate({ children }: { children: React.ReactNode }) {
  const db = useDb();
  const [gate, setGate] = useState<GateState>({ step: 'checking' });

  const check = useCallback(async () => {
    const apply = async (entitlement: Entitlement) => {
      await db.setReadOnly(!canWrite(entitlement.state));
      setGate({ step: 'ready', entitlement });
    };
    try {
      if (!(await hasCurrentConsent(db))) {
        setGate({ step: 'consent' });
        return;
      }
      // Open straight away with the plan this device already knows, then confirm it with the
      // server in the background. Only a device that has never checked waits for the network.
      const cached = await cachedEntitlement(db);
      if (cached) {
        await apply(cached);
        fetchEntitlement(db)
          .then(apply)
          .catch(() => undefined);
        return;
      }
      await apply(await loadEntitlement(db));
    } catch (error) {
      setGate({ step: 'error', message: error instanceof Error ? error.message : String(error) });
    }
  }, [db]);

  useEffect(() => {
    void check();
  }, [check]);

  // Re-check the plan when the connection comes back (e.g. after a renewal on another device).
  useEffect(() => {
    const onOnline = () => void check();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [check]);

  if (gate.step === 'checking') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-clinical-primary" aria-label="Checking your account" />
      </div>
    );
  }

  if (gate.step === 'consent') return <DisclaimerPage onAccepted={check} />;

  if (gate.step === 'error') {
    return (
      <div className="flex min-h-dvh items-center justify-center px-6 text-center">
        <div className="max-w-sm space-y-4">
          <AlertTriangle className="mx-auto h-10 w-10 text-clinical-warning" />
          <p className="text-sm text-clinical-muted">{gate.message}</p>
          <div className="flex justify-center gap-2">
            <Button onClick={() => void check()}>Try again</Button>
            <Button variant="secondary" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gate.entitlement.state === 'none') return <PaywallPage onCheckAgain={check} />;

  return <AccountContext.Provider value={{ entitlement: gate.entitlement, refreshEntitlement: check }}>{children}</AccountContext.Provider>;
}
